import { useState } from "react";
import { Star, Send, MessageCircle } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { COMPANY, ASSETS, waLink, WA_MESSAGES } from "@/lib/site";

export default function AvalieSuaViagem() {
  const [form, setForm] = useState({ nome: "", contato: "", destino: "", comentario: "", autorizo: false });
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    const msg =
      `Avaliação de viagem - Miríade Turismo%0A%0A` +
      `*Nome:* ${form.nome}%0A*Contato:* ${form.contato}%0A*Destino:* ${form.destino}%0A` +
      `*Nota:* ${rating}/5%0A*Comentário:* ${form.comentario}%0A` +
      `*Autoriza uso do depoimento:* ${form.autorizo ? "Sim" : "Não"}`;
    window.open(`https://wa.me/${COMPANY.whatsappNumber}?text=${msg}`, "_blank");
  };

  return (
    <>
      <PageHero
        overline="Sua opinião importa"
        title="Avalie sua viagem"
        subtitle="Sua opinião ajuda a Miríade Turismo a melhorar cada vez mais. Conte como foi a sua experiência."
        seoTitle="Avalie sua Viagem | Miríade Turismo"
        seoDescription="Compartilhe sua experiência de viagem com a Miríade Turismo. Sua avaliação nos ajuda a melhorar cada vez mais."
      />

      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-5">
          <Reveal className="lg:col-span-3">
            <div className="rounded-3xl border border-border bg-white p-7 shadow-premium sm:p-9">
              <h2 className="font-heading text-2xl font-bold tracking-tight text-ink">Conte como foi</h2>
              <p className="mt-2 text-sm text-ink-soft">Preencha o formulário e envie sua avaliação para a nossa equipe.</p>
              <form onSubmit={submit} data-testid="review-form" className="mt-6 space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-ink">Nome</label>
                    <input data-testid="review-name" required value={form.nome} onChange={set("nome")} className="w-full rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-brand" placeholder="Seu nome" />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-ink">Telefone ou e-mail</label>
                    <input data-testid="review-contact" required value={form.contato} onChange={set("contato")} className="w-full rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-brand" placeholder="(00) 00000-0000" />
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-ink">Destino da viagem</label>
                  <input data-testid="review-destination" required value={form.destino} onChange={set("destino")} className="w-full rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-brand" placeholder="Para onde você viajou?" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-ink">Sua nota</label>
                  <div className="flex gap-1.5" data-testid="review-stars">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        type="button"
                        key={n}
                        data-testid={`review-star-${n}`}
                        onClick={() => setRating(n)}
                        onMouseEnter={() => setHover(n)}
                        onMouseLeave={() => setHover(0)}
                        className="transition-transform hover:scale-110"
                        aria-label={`Nota ${n}`}
                      >
                        <Star size={32} className={(hover || rating) >= n ? "fill-amber-400 text-amber-400" : "text-border"} />
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-ink">Comentário</label>
                  <textarea data-testid="review-comment" rows={4} value={form.comentario} onChange={set("comentario")} className="w-full resize-none rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-brand" placeholder="Conte como foi a sua experiência..." />
                </div>
                <label className="flex items-start gap-3 text-sm text-ink-soft">
                  <input type="checkbox" data-testid="review-authorize" checked={form.autorizo} onChange={(e) => setForm((f) => ({ ...f, autorizo: e.target.checked }))} className="mt-1 h-4 w-4 rounded border-border accent-brand" />
                  Autorizo a Miríade Turismo a utilizar meu depoimento em seus canais.
                </label>
                <button type="submit" data-testid="review-submit" className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-semibold text-white transition-all hover:scale-[1.01] hover:bg-brand-hover">
                  <Send size={18} /> Enviar avaliação
                </button>
              </form>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-2">
            <div className="flex h-full flex-col items-center justify-center rounded-3xl border border-brand-light bg-brand-light/60 p-8 text-center">
              <img src={ASSETS.mascot} alt="Mascote da Miríade Turismo" className="h-40 w-40 object-contain drop-shadow-xl animate-float" />
              <h3 className="mt-5 font-heading text-xl font-semibold text-ink">Obrigado por viajar com a Miríade!</h3>
              <p className="mt-2 text-sm leading-relaxed text-ink-soft">Cada avaliação nos ajuda a cuidar ainda melhor de você e dos próximos viajantes.</p>
              <a href={waLink(WA_MESSAGES.default)} target="_blank" rel="noopener noreferrer" data-testid="review-whatsapp-btn" className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3.5 text-sm font-semibold text-white transition-all hover:scale-[1.03]">
                <MessageCircle size={18} /> Falar com a Miríade
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
